import { DataflytContactPhone, DataflytContactEmail } from 'components/dataflyt-contact';
import React from 'react';
import { Alert, Button, Checkbox, Icon, Menu, Modal, Table } from 'antd';
import DeletionImpact from './deletion-impact';
import FlightLink from './flight-link';
import PobLink from './pob-link';
import { deletionAcknowledgement } from './operation';
import { MenuItemProps } from 'antd/lib/menu/MenuItem';
import {
    PersonDeleteOptions,
    usePersonDelete
} from './use-person-delete';

type Props = PersonDeleteOptions &
    MenuItemProps & {
        /** Display label for confirmation only; never used as a deletion identifier. */
        name: string;
    };

const flightColumns = (personID: string) => [
    {
        title: 'Desig',
        key: 'desig',
        render: (_value, flight) => flight.desig || flight.id
    },
    {
        title: 'Scheduled flight date',
        dataIndex: 'scheduledFlightDate',
        render: date => date || 'Unavailable'
    },
    {
        title: 'Customer',
        dataIndex: 'customerName',
        render: name => name || 'Unavailable'
    },
    {
        title: 'Transporter',
        dataIndex: 'transporterName',
        render: name => name || 'Unavailable'
    },
    {
        title: 'Action',
        key: 'fix',
        align: 'right' as const,
        render: (_value, flight) => <FlightLink flight={flight} personID={personID} />
    }
];

const pobColumns = [
    {title: 'Person', dataIndex: 'personName', render: name => name},
    {title: 'Location', key: 'location', render: (_value, record) =>
        record.navigation?.locationName || record.navigation?.locationID || 'Unavailable'},
    {title: 'Action', key: 'fix', align: 'right' as const, render: (_value, record) => record.navigation
        ? <PobLink navigation={record.navigation} /> : 'Unavailable'}
];

/**
 * Renders the permission-controlled person-delete menu item and confirmation.
 *
 * @remarks
 * Stateful transitions and async effects live in {@link usePersonDelete}. This template
 * describes the server's America/Chicago cutoff, deleted-reference exclusions and preservation
 * rules; it does not implement those policies. Historical references remain intact, and server
 * failures (including concurrency/partial-persistence failures) are shown without client rollback.
 */
const PersonDelete: React.FC<Props> = props => {
    const { state, canDelete, unavailable, canConfirm, open, cancel, confirm, acknowledge } =
        usePersonDelete(props);
    if (!canDelete) {
        return null;
    }

    const { personID, name, onDeleted, disabled, ...menuProps } = props;
    const assignedFlights = Array.from(new Map(state.preview?.passengers
        .filter(passenger => passenger.blocker && passenger.currentFlight)
        .map(passenger => [passenger.currentFlight.id, passenger.currentFlight]) || []).values());
    const blockedPob = state.preview?.pobID && state.preview.blockers?.some(blocker => blocker.id === state.preview.pobID)
        ? [{id: state.preview.pobID, personName: state.preview.personName, navigation: state.preview.pobNavigation}] : [];
    const blockerReasons = Array.from(new Set(state.preview?.blockers?.map(blocker => blocker.reason) || []));
    const confirmation = (
        <span onClick={event => event.stopPropagation()}>
            <Modal
                visible={state.visible}
                width={720}
                style={{top: 32}}
                bodyStyle={{maxHeight: 'calc(100vh - 180px)', overflowY: 'auto'}}
                title={`Delete person: ${props.name}?`}
                okText="Delete person"
                okType="danger"
                confirmLoading={state.busy}
                onOk={confirm}
                onCancel={cancel}
                maskClosable={!state.busy}
                closable={!state.busy}
                keyboard={!state.busy}
                cancelButtonProps={{ disabled: state.busy }}
                okButtonProps={{ disabled: !canConfirm }}
            >
                <div className="person-deletion-intro">
                    <p>Delete this person and the records marked for deletion, and clear their references from the records marked for update.</p>
                    {!state.loading && <Button icon="reload" onClick={open} disabled={state.busy || state.deleted}>
                        Refresh preview
                    </Button>}
                </div>
                {state.loading && <div role="status"><Icon type="loading" spin /> Checking records and flight assignments…</div>}
                {state.preview?.blockingMessage && (
                    <Alert type="warning" showIcon message="Deletion blocked"
                        description={<>
                            <ul className="person-deletion-blockers">
                                {blockerReasons.map(reason => <li key={reason}>{reason}</li>)}
                            </ul>
                            {state.preview.supportRequired && (
                                <p>Please contact Dataflyt at <DataflytContactPhone /> or{' '}
                                    <DataflytContactEmail /> for assistance.</p>
                            )}
                            {!!assignedFlights.length && <section className="person-deletion-blocker-table" aria-label="Flights">
                                <h4>Flights</h4>
                                <Table
                                    size="small"
                                    pagination={false}
                                    rowKey="id"
                                    columns={flightColumns(personID)}
                                    dataSource={assignedFlights}
                                    scroll={{x: 520}}
                                />
                            </section>}
                            {!!blockedPob.length && <section className="person-deletion-blocker-table" aria-label="POB records">
                                <h4>POB records</h4>
                                <Table
                                    size="small"
                                    pagination={false}
                                    rowKey="id"
                                    columns={pobColumns}
                                    dataSource={blockedPob}
                                />
                            </section>}
                        </>} />
                )}
                {state.preview && <DeletionImpact preview={state.preview} />}
                {state.preview && <div>
                    <Checkbox checked={state.acknowledged} disabled={state.busy}
                        onChange={event => acknowledge(event.target.checked)}>
                        {deletionAcknowledgement(state.preview)}
                    </Checkbox>
                </div>}
                {state.error && (
                    <Alert
                        type="error"
                        showIcon
                        message="Unable to delete person"
                        description={state.error}
                    />
                )}
            </Modal>
        </span>
    );

    return (
        <Menu.Item
            {...menuProps}
            disabled={unavailable}
            onClick={event => {
                open();
                menuProps.onClick?.(event);
            }}
        >
            Delete person
            {confirmation}
        </Menu.Item>
    );
};

export default PersonDelete;
